// Seasons, weather and time of day (2026-10-26; tinwhistlegames-ops docs/games/footies VISUALS.md section 3, "just like
// we have in sheep tag"). Ported from Sheep Tag: its render/theme.ts (a season per match), weather.ts (the timeline, as
// is) and scene.ts setSky (the day as stops, the weather's grade on top). Visual only: the sim never reads any of it.
// All of it is a pure function of the match seed and the match clock, so every client sees the same sky with no
// network data; nothing here reads Math.random or the wall clock (tests/sky.test.ts holds it to that).
//   Season: one per match from the seed. Summer is the look the world pass built (palette.ts as it ships); the others
//   paint over it once a match (paintSeason): spring's fresh greens, blossom and flowers, autumn's reds and golds and
//   fewer flowers, winter's snow (a base cover that weather adds to), bare oaks and birches, iced river edges.
//   Day: the match runs morning to midday to a golden dusk at the hard end (BASE.decayFrom, minute 18), night if it runs
//   long; some matches open at dawn or in the afternoon. The hour drives the sun's place, color and strength, the
//   sky, fog and fill, and the lights (windows, lanterns, the team discs' glow).
//   Weather: clear, overcast, rain or snow, clearing, each blended over 20 to 40 s; wet ground and snow cover lag the
//   sky. Settings, Weather effects Off drops it to clear (the season and the hour stay).
// paint() writes it all into palette.ts's Colors and look.ts's shared uniforms, which every material reads by
// reference, and hands the Stage its numbers. ?season= ?weather= ?tod= ?hour= ?clock= pin any of it (skyPin).
import * as THREE from 'three';
import { BASE } from '../sim/defs';
import { PALETTE } from './palette';
import { shared, type StageLight } from './look';
import { TREE_SEASON, type TreeKind } from './foliage';

export type Season = 'spring' | 'summer' | 'autumn' | 'winter';
export const SEASONS: readonly Season[] = ['spring', 'summer', 'autumn', 'winter'];
/** How a match's day opens: most in the morning, some at dawn, a few in the afternoon (all reach dusk at the hard end). */
export type Opening = 'morning' | 'dawn' | 'afternoon';

/** A number in [0, 1) from the seed and a salt (an integer mix, the same on every machine). */
function mix(seed: number, salt: number) {
  let h = Math.imul((seed | 0) ^ Math.imul(salt, 0x9e3779b1), 0x85ebca6b);
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
export const seasonFor = (seed: number): Season => SEASONS[Math.floor(mix(seed, 1) * 4)];
export const openingFor = (seed: number): Opening => { const r = mix(seed, 2); return r < 0.25 ? 'dawn' : r < 0.37 ? 'afternoon' : 'morning'; };

// ---- the day: match minutes to the hour (0 to 24, past 24 is after midnight)
const END = BASE.decayFrom / 60;
/** From the middle of the match on, every opening runs the same: midday, the afternoon, a golden dusk as the hard end
 *  starts, the blue hour, night. */
const LATE: [number, number][] = [[13, 14.5], [16, 16.9], [END, 18.3], [END + 1.5, 19.4], [END + 3, 20.3], [END + 4.5, 21.2]];
export const DAYS: Record<Opening, [number, number][]> = {
  morning: [[0, 8], [3, 9.5], [6, 11.5], ...LATE],
  dawn: [[0, 5.9], [2.5, 8], [6, 11.5], ...LATE],
  afternoon: [[0, 14.6], [8, 15.6], ...LATE.slice(1)],
};
/** The hour at match second t for an opening. */
export function hourAt(o: Opening, t: number) {
  const k = DAYS[o], m = Math.max(0, t) / 60;
  if (m >= k[k.length - 1][0]) return k[k.length - 1][1];
  let i = 0;
  while (m > k[i + 1][0]) i++;
  const [m0, h0] = k[i], [m1, h1] = k[i + 1];
  return h0 + ((h1 - h0) * (m - m0)) / (m1 - m0);
}
/** Named hours for ?tod= and the stills. */
export const TOD: Record<string, number> = { dawn: 6.2, morning: 7.8, midday: 12.5, afternoon: 16, golden: 18.3, dusk: 19.4, night: 21.5 };

/** The light at an hour: the sky at the horizon and overhead, the sun (the moon at night: its color, strength and
 *  place as azimuth and elevation in degrees, azimuth round from +x toward +z, the camera's side), the fill from above
 *  and how much the ground bounces (times the season's ground), exposure. The midday stop is the world pass's light
 *  exactly (palette.ts as it ships), so a summer midday looks as it did. STYLE.md: sunny by default, never dark;
 *  night is a friendly moonlit blue that keeps every color readable. Sheep Tag's lesson: a sun grazing the ground
 *  reads murky, so dusk keeps it about 20 degrees up and lets the color do the work. */
type Stop = { h: number; horizon: number; zenith: number; sun: number; sunI: number; fill: number; fillI: number; bounce: number; az: number; el: number; exp: number };
const NIGHT = { horizon: 0x2c3c72, zenith: 0x142052, sun: 0xa8bcff, sunI: 0.9, fill: 0x8496dc, fillI: 0.95, bounce: 0.42, az: 118, el: 52, exp: 1.16 };
export const DAY: Stop[] = [
  { h: 5, ...NIGHT },
  { h: 5.6, horizon: 0x7480b0, zenith: 0x34427a, sun: 0xb8b8f0, sunI: 0.6, fill: 0x9a9ad0, fillI: 0.95, bounce: 0.5, az: 70, el: 36, exp: 1.18 },
  { h: 6.2, horizon: 0xf6c4a6, zenith: 0x8ea8d8, sun: 0xffc49c, sunI: 1.45, fill: 0xdcd4f0, fillI: 0.9, bounce: 0.72, az: 22, el: 22, exp: 1.06 },
  { h: 7.6, horizon: 0xf2dcc4, zenith: 0x86b4e4, sun: 0xffe0bc, sunI: 1.8, fill: 0xd8e4fa, fillI: 0.92, bounce: 0.9, az: 45, el: 34, exp: 1.02 },
  { h: 10, horizon: 0xdbecf5, zenith: 0x72b2e8, sun: 0xfff0d8, sunI: 1.95, fill: 0xd0e3ff, fillI: 0.95, bounce: 1, az: 100, el: 50, exp: 1 },
  { h: 12.5, horizon: 0xd9ecf7, zenith: 0x6fb2ea, sun: 0xfff0d6, sunI: 2.0, fill: 0xcfe3ff, fillI: 0.95, bounce: 1, az: 142.62, el: 55.31, exp: 1 },
  { h: 16, horizon: 0xe2e8ec, zenith: 0x72ace2, sun: 0xffe8cc, sunI: 1.95, fill: 0xd4e0f6, fillI: 0.93, bounce: 1, az: 158, el: 44, exp: 1 },
  { h: 18.3, horizon: 0xf6c896, zenith: 0x7f9ece, sun: 0xffbe7c, sunI: 1.85, fill: 0xead4c4, fillI: 0.88, bounce: 0.92, az: 166, el: 27, exp: 1.04 },
  { h: 19.4, horizon: 0xf0a078, zenith: 0x5e6eaa, sun: 0xffa874, sunI: 1.45, fill: 0xb0b8e4, fillI: 1.0, bounce: 0.75, az: 172, el: 20, exp: 1.1 },
  { h: 20.3, horizon: 0x6a6a9e, zenith: 0x2a3874, sun: 0xb0b0e8, sunI: 0.62, fill: 0x8a98d8, fillI: 0.95, bounce: 0.52, az: 150, el: 36, exp: 1.16 },
  { h: 21.2, ...NIGHT },
  { h: 29, ...NIGHT },
];

// ---- weather (Sheep Tag render/weather.ts, the timeline as is; its seasons mapped to ours)
export interface Wx {
  /** 0 clear .. 1 heavy overcast */
  cloud: number;
  /** precipitation intensity, 0..1 */
  rain: number;
  snow: number;
  /** 0 calm .. about 1.3 gusty */
  wind: number;
  /** ground wetness and snow cover, 0..1, both lag the sky */
  wet: number;
  cover: number;
}
export const CLEAR_WX: Wx = { cloud: 0, rain: 0, snow: 0, wind: 0.4, wet: 0, cover: 0 };
/** ?weather= pins: the sky and the ground as they are a while into each. */
export const WEATHERS: Record<string, Wx> = {
  clear: { cloud: 0.06, rain: 0, snow: 0, wind: 0.3, wet: 0, cover: 0 },
  overcast: { cloud: 0.78, rain: 0, snow: 0, wind: 0.6, wet: 0, cover: 0 },
  rain: { cloud: 0.95, rain: 0.85, snow: 0, wind: 0.9, wet: 1, cover: 0 },
  snow: { cloud: 0.9, rain: 0, snow: 0.85, wind: 0.8, wet: 0, cover: 1 },
};

type Kind = 'clear' | 'overcast' | 'precip' | 'clearing';
interface Seg { kind: Kind; start: number; blend: number; cloud: number; rain: number; snow: number; wind: number }

function rng(seed: number) {
  let a = (seed ^ 0x5eedbeef) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** How likely a shower falls as snow. */
export const SNOW_CHANCE: Record<Season, number> = { spring: 0.04, summer: 0, autumn: 0.15, winter: 0.85 };
const STEP = 0.5; // seconds per table row
/** The longest match the timeline covers (past it, it holds its last row): the hard end plus a long night. */
const SPAN = 30 * 60;

export class WeatherTimeline {
  private segs: Seg[] = [];
  private table: Float32Array; // 6 floats per row: cloud rain snow wind wet cover
  private rows: number;

  constructor(seed: number, season: Season) {
    const r = rng(Math.imul(seed | 0, 2654435761));
    // about a quarter of matches stay dry from start to finish
    const dry = r() < 0.25;
    const order: Kind[] = ['clear', 'overcast', 'precip', 'clearing'];
    const pick = r();
    let i = pick < 0.5 ? 0 : pick < 0.78 ? 1 : pick < 0.9 ? 2 : 3;
    let t = 0;
    while (t < SPAN) {
      const kind = order[i % 4];
      // these matches run 20 minutes or more, against Sheep Tag's 10: each spell lasts about twice as long
      const len = 2 * (kind === 'clear' ? 55 + r() * 85 : kind === 'overcast' ? 35 + r() * 40 : kind === 'precip' ? 50 + r() * 60 : 30 + r() * 30);
      const snowy = r() < SNOW_CHANCE[season];
      const amt = 0.55 + r() * 0.45;
      const s: Seg = { kind, start: t, blend: 20 + r() * 20, cloud: 0, rain: 0, snow: 0, wind: 0.35 };
      if (kind === 'clear') { s.cloud = 0.04 + r() * 0.16; s.wind = 0.25 + r() * 0.2; }
      else if (kind === 'overcast') { s.cloud = 0.62 + r() * 0.2; s.wind = 0.5 + r() * 0.25; }
      else if (kind === 'precip') {
        s.cloud = 0.92;
        s.wind = 0.75 + r() * 0.5;
        if (dry) { s.cloud = 0.7; s.wind = 0.6; }
        else if (snowy) s.snow = amt; else s.rain = amt;
      } else { s.cloud = 0.4 + r() * 0.15; s.wind = 0.45; }
      // winter keeps a light flurry even when the sky is clear
      if (season === 'winter' && s.snow < 0.14 && s.rain === 0) s.snow = 0.14;
      this.segs.push(s);
      t += len;
      i++;
    }
    // bake the sky and integrate the ground (wet, cover) once, at half-second steps
    this.rows = Math.ceil(SPAN / STEP) + 1;
    this.table = new Float32Array(this.rows * 6);
    let wet = 0, cover = 0;
    for (let k = 0; k < this.rows; k++) {
      const sky = this.sky(k * STEP);
      const rainy = sky.rain > 0.2 ? sky.rain : 0;
      wet += rainy > 0 ? (1 - wet) * rainy * STEP / 22 : -wet * STEP / 75;
      // snow settles slowly, rain washes it away, and it melts very slowly when the sky clears
      const melt = season === 'winter' ? 260 : 110;
      cover += sky.snow > 0.2 ? (1 - cover) * sky.snow * STEP / 45 : -cover * STEP / melt;
      cover -= cover * rainy * STEP / 40;
      const o = k * 6;
      this.table[o] = sky.cloud; this.table[o + 1] = sky.rain; this.table[o + 2] = sky.snow;
      this.table[o + 3] = sky.wind; this.table[o + 4] = wet; this.table[o + 5] = cover;
    }
  }

  /** The sky alone at time t: segment values with a smooth blend into each new segment. */
  private sky(t: number) {
    let j = 0;
    while (j + 1 < this.segs.length && this.segs[j + 1].start <= t) j++;
    const cur = this.segs[j], prev = this.segs[j - 1];
    const out = { cloud: cur.cloud, rain: cur.rain, snow: cur.snow, wind: cur.wind };
    if (prev) {
      const x = Math.min(1, (t - cur.start) / cur.blend);
      const k = x * x * (3 - 2 * x);
      // precipitation trails the clouds on the way in, and leaves first on the way out
      const kp = cur.kind === 'precip' ? Math.max(0, (k - 0.35) / 0.65) : Math.min(1, k / 0.6);
      out.cloud = prev.cloud + (cur.cloud - prev.cloud) * k;
      out.wind = prev.wind + (cur.wind - prev.wind) * k;
      out.rain = prev.rain + (cur.rain - prev.rain) * kp;
      out.snow = prev.snow + (cur.snow - prev.snow) * kp;
    }
    return out;
  }

  at(t: number, into: Wx = { ...CLEAR_WX }): Wx {
    const f = Math.max(0, Math.min(this.rows - 1.001, t / STEP));
    const k = Math.floor(f), u = f - k, a = k * 6, b = a + 6, T = this.table;
    into.cloud = T[a] + (T[b] - T[a]) * u;
    into.rain = T[a + 1] + (T[b + 1] - T[a + 1]) * u;
    into.snow = T[a + 2] + (T[b + 2] - T[a + 2]) * u;
    into.wind = T[a + 3] + (T[b + 3] - T[a + 3]) * u;
    into.wet = T[a + 4] + (T[b + 4] - T[a + 4]) * u;
    into.cover = T[a + 5] + (T[b + 5] - T[a + 5]) * u;
    return into;
  }
}

// ---- seasons: what each paints over summer (palette.ts as it ships)
type Paint = { [group: string]: { [key: string]: number | number[] } };
export const SEASON_PAINT: Record<Season, Paint> = {
  summer: {},
  spring: {
    ground: { grassA: 0x66a83e, grassB: 0x8dc652, forest: 0x46803a, side: 0x47803a },
    grass: { root: 0x55893a, tip: 0xb6da68, dry: 0xd2e08c },
    foliage: { oak: [0x5a9e32, 0xa8d45e], birch: [0x7cbc3c, 0xd2e87e], bush: [0x52983a, 0xa4d266] },
    accent: { oak: 0xf8c6d8, birch: 0xf8c6d8, bush: 0xfbf6ee },
    scatter: { mound: 0x66a84a },
  },
  autumn: {
    ground: { grassA: 0x7c9238, grassB: 0xa0a64a, forest: 0x606030, side: 0x5e6c30, sheen: 0x4a4420, edge: 0xa69252 },
    grass: { root: 0x6c7c32, tip: 0xcab85c, dry: 0xdcb468 },
    foliage: { oak: [0xc4502a, 0xeaa23c], birch: [0xd6a22e, 0xf4d45c], pine: [0x2c5c3e, 0x5c8a4c], bush: [0xb0482a, 0xdc7c30] },
    accent: { oak: 0xa82c24, birch: 0xe0a02c, bush: 0xc23c26 },
    bark: { oak: 0x4f3a2a },
    scatter: { mound: 0x8a8c3c },
  },
  winter: {
    ground: { grassA: 0x6a8a58, grassB: 0x86a26c, forest: 0x4a6642, sandA: 0xd2c6a8, sandB: 0xc0b090, edge: 0xa8a290, bank: 0x6a6258 },
    grass: { root: 0x5c7a4a, tip: 0xa2b48c, dry: 0xc8c4a2 },
    foliage: { pine: [0x22503a, 0x4a7a56], bush: [0x36583e, 0x62845e] },
    water: { deep: 0x2c6c9e, shallow: 0x84bcda, foam: 0xf4faff },
    scatter: { mound: 0x5c7a50 },
  },
};
/** Per season and tree kind: the share of leaves gone (winter strips the broadleaves) and the share of trees in the
 *  kind's accent color (spring blossom, autumn's deep reds). */
export const SEASON_TREES: Record<Season, { bare: Partial<Record<TreeKind, number>>; frost: Partial<Record<TreeKind, number>>; accent: Partial<Record<TreeKind, number>> }> = {
  spring: { bare: {}, frost: {}, accent: { oak: 0.3, bush: 0.3 } },
  summer: { bare: {}, frost: {}, accent: {} },
  autumn: { bare: { oak: 0.12, birch: 0.18 }, frost: {}, accent: { oak: 0.35, birch: 0.25, bush: 0.3 } },
  winter: { bare: { oak: 0.55, birch: 0.6, bush: 0.3 }, frost: { oak: 1, birch: 1, bush: 0.5 }, accent: {} },
};
/** Each season's share of the verge's flowers out, the snow it starts with (weather adds to it), whether the river's
 *  edges freeze, and what drifts down through it (falling leaves, blossom petals) and how much. */
export const SEASON_LOOK: Record<Season, { flowers: number; cover: number; ice: number; drift: number }> = {
  spring: { flowers: 1, cover: 0, ice: 0, drift: 0.18 },
  summer: { flowers: 0.65, cover: 0, ice: 0, drift: 0 },
  autumn: { flowers: 0.25, cover: 0, ice: 0, drift: 0.5 },
  winter: { flowers: 0, cover: 0.72, ice: 1, drift: 0 },
};

/** Summer: the palette as it shipped, kept so any season paints back from it (whole Colors: the glow is over 1). */
const SUMMER = new Map<THREE.Color, THREE.Color>();
const walk = (o: unknown, fn: (c: THREE.Color) => void) => {
  if (o instanceof THREE.Color) fn(o);
  else if (Array.isArray(o)) o.forEach((x) => walk(x, fn));
  else if (o && typeof o === 'object' && !(o instanceof THREE.Vector3)) Object.values(o).forEach((x) => walk(x, fn));
};
walk(PALETTE, (c) => SUMMER.set(c, c.clone()));
const SUMMER_SUN = PALETTE.sun.dir.clone(), SUMMER_GLOW = PALETTE.glow.clone(), SUMMER_LAMP = PALETTE.lamp.clone();
/** A palette color as summer has it (the minimap moves its own colors as far as the season moved the world's). */
export const summerOf = (c: THREE.Color) => SUMMER.get(c) ?? c;

/** Paints a season's colors into the palette (from summer, so seasons never stack) and its trees, flowers and ice
 *  into the shared uniforms. Once a match. */
export function paintSeason(season: Season) {
  for (const [c, was] of SUMMER) c.copy(was);
  const P = PALETTE as unknown as Record<string, Record<string, THREE.Color | THREE.Color[]>>;
  for (const [group, keys] of Object.entries(SEASON_PAINT[season])) for (const [key, v] of Object.entries(keys)) {
    const at = P[group][key];
    if (Array.isArray(at)) (v as number[]).forEach((hex, k) => at[k].setHex(hex));
    else at.setHex(v as number);
  }
  for (const kind of Object.keys(TREE_SEASON) as TreeKind[]) {
    TREE_SEASON[kind].uBare.value = SEASON_TREES[season].bare[kind] ?? 0;
    TREE_SEASON[kind].uFrost.value = SEASON_TREES[season].frost[kind] ?? 0;
    TREE_SEASON[kind].uAccent.value = SEASON_TREES[season].accent[kind] ?? 0;
  }
  shared.uFlowers.value = SEASON_LOOK[season].flowers;
  shared.uIce.value = SEASON_LOOK[season].ice;
}

/** What the sky is at a moment: the season, the hour, the weather, the snow lying, the lights' share and what
 *  drifts down. */
export type SkyState = { season: Season; opening: Opening; hour: number; wx: Wx; cover: number; lamps: number; drift: number };
/** Pins from the address or the QA hook: any of them holds that part still (null: follow the match). */
export type Pin = { season: Season | null; weather: Wx | null; hour: number | null; clock: number | null };
export const NO_PIN: Pin = { season: null, weather: null, hour: null, clock: null };
/** ?season=winter ?weather=clear|overcast|rain|snow ?tod=dawn|morning|midday|afternoon|golden|dusk|night (or ?hour=19.5)
 *  ?clock=<minutes> (the whole sky as at that match minute: hour and weather). */
export function skyPin(q: URLSearchParams): Pin {
  const season = q.get('season') as Season | null, w = q.get('weather'), tod = q.get('tod'), hour = q.get('hour'), clock = q.get('clock');
  return {
    season: season && SEASONS.includes(season) ? season : null,
    weather: w && WEATHERS[w] ? { ...WEATHERS[w] } : null,
    hour: tod && TOD[tod] !== undefined ? TOD[tod] : hour !== null && Number.isFinite(Number(hour)) ? Number(hour) : null,
    clock: clock !== null && Number.isFinite(Number(clock)) ? Number(clock) * 60 : null,
  };
}

const smooth = (a: number, b: number, x: number) => { const k = Math.min(1, Math.max(0, (x - a) / (b - a))); return k * k * (3 - 2 * k); };

/** One match's sky. */
export class Sky {
  readonly season: Season;
  readonly opening: Opening;
  readonly timeline: WeatherTimeline;
  constructor(readonly seed: number, readonly pin: Pin = NO_PIN) {
    this.season = pin.season ?? seasonFor(seed);
    this.opening = openingFor(seed);
    this.timeline = new WeatherTimeline(seed, this.season);
  }
  /** The sky at match second t; weather false is Settings, Weather effects Off (clear, the season's own snow stays). */
  at(t: number, weather = true, out?: SkyState): SkyState {
    const at = this.pin.clock ?? t, look = SEASON_LOOK[this.season];
    const hour = this.pin.hour ?? hourAt(this.opening, at);
    let wx = this.pin.weather ? { ...this.pin.weather } : this.timeline.at(at, out?.wx);
    if (!weather) wx = { ...CLEAR_WX, wind: wx.wind };
    const cover = look.cover + (1 - look.cover) * wx.cover;
    // the lights come on from the golden hour and are full by the blue hour, go out after dawn; a storm lights them early
    const h = ((hour - 5) % 24 + 24) % 24 + 5;
    const lamps = Math.max(smooth(17.9, 19.9, h) , 1 - smooth(5.8, 7.2, h), 0.45 * Math.max(wx.rain, wx.snow) * wx.cloud);
    const s = out ?? ({} as SkyState);
    s.season = this.season; s.opening = this.opening; s.hour = hour; s.wx = wx; s.cover = cover; s.lamps = Math.min(1, lamps);
    s.drift = weather ? look.drift : 0;
    return s;
  }
}

const _a = new THREE.Color(), _b = new THREE.Color(), _g = new THREE.Color();
const lerpHex = (out: THREE.Color, a: number, b: number, e: number) => out.setHex(a).lerp(_b.setHex(b), e);

/** Paints the sky's light into the palette and the shared uniforms, and returns the Stage's numbers. Pure apart from
 *  those writes: the same state always paints the same. */
export function paint(s: SkyState): StageLight {
  const h = ((s.hour - 5) % 24 + 24) % 24 + 5;
  let i = 0;
  while (i < DAY.length - 2 && h > DAY[i + 1].h) i++;
  const a = DAY[i], b = DAY[i + 1], x = Math.min(1, Math.max(0, (h - a.h) / (b.h - a.h))), e = x * x * (3 - 2 * x);
  const { cloud, rain, snow, wet } = s.wx;
  const P = PALETTE;
  lerpHex(P.sky.horizon, a.horizon, b.horizon, e); lerpHex(P.sky.zenith, a.zenith, b.zenith, e);
  lerpHex(P.sun.color, a.sun, b.sun, e); lerpHex(P.fill.sky, a.fill, b.fill, e);
  let sunI = a.sunI + (b.sunI - a.sunI) * e, fillI = a.fillI + (b.fillI - a.fillI) * e;
  const az = THREE.MathUtils.degToRad(a.az + (b.az - a.az) * e), el = THREE.MathUtils.degToRad(a.el + (b.el - a.el) * e);
  // at the midday stop exactly, the world pass's own sun (its stored direction, not one rebuilt from rounded angles)
  if (h === 12.5) P.sun.dir.copy(SUMMER_SUN);
  else P.sun.dir.set(Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el));
  // how far into the night (the blue hour to the small hours): clouds and the light map ease off in the dark
  const night = Math.max(smooth(19.6, 21, h), 1 - smooth(5.6, 6.6, h));
  // the fill bounced from the ground: the summer grass, darker at night, whiter under snow
  P.fill.ground.copy(SUMMER.get(P.fill.ground)!).multiplyScalar(a.bounce + (b.bounce - a.bounce) * e)
    .lerp(_g.setHex(0xc8d0dc).multiplyScalar(1 - 0.5 * night), s.cover * 0.45);
  // weather (Sheep Tag setSky): overcast greys the sky and trades sun for sky light; rain and snow pull the fog in
  const c = cloud * (1 - 0.5 * night);
  for (const col of [P.sky.horizon, P.sky.zenith]) {
    const l = col.r * 0.299 + col.g * 0.587 + col.b * 0.114;
    col.lerp(_a.setRGB(l * 0.9, l * 0.93, l * 0.98), c * 0.72);
  }
  sunI *= 1 - 0.62 * c;
  fillI *= 1 + 0.22 * c;
  P.fill.sky.lerp(_a.setRGB(0.76, 0.8, 0.88), c * 0.45);
  P.sun.intensity = sunI; P.fill.intensity = fillI;
  // the lights: windows brighter, lanterns lit, the discs glowing in their own color (more on snow, VISUALS.md)
  P.glow.copy(SUMMER_GLOW).multiplyScalar(1 + 0.9 * s.lamps);
  P.lamp.copy(SUMMER_LAMP).multiplyScalar(1 + 7 * s.lamps);
  shared.uLamps.value = s.lamps;
  shared.uDiscGlow.value = 0.32 * s.lamps + 0.12 * s.cover;
  shared.uCover.value = s.cover;
  shared.uWet.value = wet;
  shared.uRain.value = rain;
  const p = Math.max(rain, snow * 0.8);
  return {
    sun: sunI, fill: fillI, exposure: (a.exp + (b.exp - a.exp) * e) * (1 - 0.05 * c), shadow: 1 - 0.6 * c, fog: 1 - 0.42 * p,
    sat: 1.12 * (1 - 0.18 * c), env: 1 - 0.35 * night,
    // the light map follows the sky by steps: a fifth of an hour, a tenth of the cloud, a fifth of the snow
    envKey: `${s.season}|${Math.round(h * 5)}|${Math.round(cloud * 10)}|${Math.round(s.cover * 5)}`,
  };
}
